import { createFileRoute } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { Typography } from '@interxfi/design-system/components/typography';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import { AccountControlSummary } from '../../../../../../../../../features/registration/account/controls/account-control-summary';
import { AccountPlatformControlSummary } from '../../../../../../../../../features/registration/account/controls/account-platform-control-summary';
import { RegistrationAccountOverviewTabbedNavigation } from '../../../../../../../../../features/registration/account/registration-account-overview-tabbed-navigation';
import { RegistrationAccountUpdateControlsDrawer } from '../../../../../../../../../features/registration/account/registration-account-update-controls-drawer';
import { RegistrationPlatformAccountUpdateControlsDrawer } from '../../../../../../../../../features/registration/account/registration-platform-account-update-controls-drawer';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_overview/detail/controls')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-${params.accountId}-controls`,
        label: 'Account Details',
        to: `/registration/account/${params.accountId}/detail/controls`,
      }),
    }) satisfies AggregateBreadcrumb,
  staticData: {
    tab: {
      group: TabbedNavigationGroup.RegistrationAccountOverview,
      label: 'Controls',
      order: 3,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.RegistrationAccountOverview>,
  head: () => getPageHead('Account Controls'),
  component: AccountControlDetails,
});

function AccountControlDetails() {
  const { accountId } = Route.useParams();
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const { data, isPending, error } = useIxcRegistrationV1AccountQueryServiceAccountGet({ accountId });
  const showPending = Boolean(isPending || error);
  const showAccountEditAction = activeTenantIsClearing || (data?.account?.data?.spec && !('platform' in data.account.data.spec));

  return (
    <VerticalStack gap={7}>
      <RegistrationAccountOverviewTabbedNavigation
        accountId={accountId}
        actions={
          showAccountEditAction ? (
            <RequiresAccess write>
              {data?.account?.data?.spec && 'platform' in data.account.data.spec ? (
                <RegistrationPlatformAccountUpdateControlsDrawer accountId={accountId} trigger={<Button size="small">Edit</Button>} />
              ) : (
                <RegistrationAccountUpdateControlsDrawer accountId={accountId} trigger={<Button size="small">Edit</Button>} />
              )}
            </RequiresAccess>
          ) : undefined
        }
      />

      <AccountControlSummary
        clearingControls={data?.account?.data?.clearingControls}
        controls={data?.account?.data?.controls}
        isPending={showPending}
        programId={data?.account?.programId}
      />

      {data?.account?.data?.spec && 'platform' in data.account.data.spec ? (
        <VerticalStack gap={4}>
          <Typography.Level3>Platform Controls</Typography.Level3>
          <AccountPlatformControlSummary controls={data.account.data.spec.platform.controls} isPending={showPending} />
        </VerticalStack>
      ) : null}
    </VerticalStack>
  );
}
